<script setup>
import { ref, onBeforeUnmount } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();

// Auto-retry do preview de vídeo (mesma motivação do chip de imagem: o preview pode ser
// pedido antes de o blob estar servível no envio). Feito DIRETO no <video> (não via
// useLoadWithRetry, que usa `new Image()`) para NÃO baixar o vídeo duas vezes: recarrega
// o próprio elemento (bump de :key) só quando dá erro. Máx 3 tentativas (0 → +1s → +2s);
// se esgotar, vira botão de tentar novamente — recupera sem recarregar a página.
const MAX_RETRY = 3;
const BACKOFF = 1000;

const hasError = ref(false);
const isRetrying = ref(false);
const reloadKey = ref(0);
let attempt = 0;
let retryTimer = null;

const handleError = () => {
  if (hasError.value) return;

  if (attempt < MAX_RETRY - 1) {
    attempt += 1;
    isRetrying.value = true;
    retryTimer = setTimeout(() => {
      reloadKey.value += 1; // recria o <video> -> recarrega a src
    }, BACKOFF * attempt);
  } else {
    hasError.value = true;
    isRetrying.value = false;
  }
};

const handleLoaded = () => {
  attempt = 0;
  hasError.value = false;
  isRetrying.value = false;
};

const retryManual = () => {
  attempt = 0;
  hasError.value = false;
  isRetrying.value = true;
  reloadKey.value += 1;
};

onBeforeUnmount(() => {
  if (retryTimer) clearTimeout(retryTimer);
});
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer relative group"
    @click="showGallery = true"
  >
    <button
      v-if="hasError"
      type="button"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11 disabled:cursor-wait"
      :disabled="isRetrying"
      @click.stop="retryManual"
    >
      <Icon
        :icon="isRetrying ? 'i-lucide-loader-circle' : 'i-lucide-rotate-cw'"
        :class="{ 'animate-spin': isRetrying }"
        class="text-n-slate-11"
      />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </button>
    <template v-else>
      <video
        :key="reloadKey"
        :src="attachment.dataUrl"
        class="w-full h-full object-cover"
        muted
        playsInline
        @error="handleError"
        @loadeddata="handleLoaded"
      />
      <div
        class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
      >
        <div
          class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
        >
          <Icon
            icon="i-teenyicons-play-small-solid"
            class="size-7 text-n-slate-12/80 backdrop-blur"
          />
        </div>
      </div>
    </template>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="hasError = true"
    @close="() => (showGallery = false)"
  />
</template>
